import { Box } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

import { Background } from '@/components/ui/Background';
import { Layout, Header } from '@/components/layout';
import { Icon, Row, Text, MobileButton, MobileInput } from '@/components/ui';
import { ProjectCard } from '../components';
import { useProjectsStore } from '@/features/projects/store';
import { useFooterContext } from '@/components/layout';
import newProjectIcon from '@/assets/new-project-icon.svg';

export const ProjectsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projects } = useProjectsStore();
  const { getProjects } = useProjectsStore(state => state.actions);
  const [searchText, setSearchText] = useState('');
  const { setFooterPlusButton } = useFooterContext();

  // Filter projects by search text
  const filteredProjects = useMemo(() => {
    if (!searchText) return projects;
    const searchLower = searchText.toLowerCase().trim();
    return projects.filter(
      project =>
        project.projectName.toLowerCase().includes(searchLower) ||
        (project.description &&
          project.description.toLowerCase().includes(searchLower))
    );
  }, [projects, searchText]);

  useEffect(() => {
    getProjects();
    // Set plus button for projects page
    setFooterPlusButton(true, handleAddProject);

    return () => {
      setFooterPlusButton(false);
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  const handleSelectedProject = (id: number) => {
    navigate({ to: `/projects/${id}/sites`, search: {} });
  };

  const handleAddProject = () => {
    navigate({ to: '/projects/new' });
  };

  return (
    <Layout>
      <Box sx={{ position: 'relative' }}>
        <Header title={t('irrigation_system_survey')} hideBackButton={true} />

        <Box>
          <Row
            justifyContent={'center'}
            alignItems={'center'}
            sx={{ mb: '1rem' }}
          >
            <MobileButton
              btnColor='primary.main'
              sx={{ width: '100%', height: '2.5rem' }}
              onClick={handleAddProject}
            >
              <Icon
                height={'1.5rem'}
                width={'1.5rem'}
                sx={{ mr: '0.5rem' }}
                src={newProjectIcon}
              />
              <Text color='white'>{t('new_project')}</Text>
            </MobileButton>
          </Row>

          <Box>
            <MobileInput
              label=''
              name='search'
              error={false}
              helperText=''
              onChange={e => handleSearchChange(e.target.value)}
              value={searchText}
              search
              compact
              placeholder={t('search_placeholder') || ''}
            />

            {filteredProjects.length ? (
              <Box sx={{ marginTop: '1rem' }}>
                {filteredProjects.map(item => (
                  <Box key={`project-${item.id}`}>
                    <ProjectCard
                      project={item}
                      handleClick={() => handleSelectedProject(item.id)}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Text sx={{ color: 'grey.500' }}>
                  {projects.length === 0
                    ? t('no_projects')
                    : t('no_search_results')}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Background />
    </Layout>
  );
};
